import React, { useEffect, useRef, useState } from 'react';
import WaveSurfer from 'wavesurfer.js';
import RegionsPlugin from 'wavesurfer.js/dist/plugin/wavesurfer.regions.min.js';
import { Button } from 'react-bootstrap';
import './AudioTrimmer.scss';
import LoadingIndicator from '@/components/LoadingIndicator.js';

const formatTime = (seconds) => {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
};

// 剪辑时长限制 5秒到5分钟
export const MIN_CLIP_DURATION = 5;
export const MAX_CLIP_DURATION = 300;

const AudioTrimmer = ({ file, onTrimmed }) => {
  const containerRef = useRef();
  const wrapperRef = useRef();
  const wavesurferRef = useRef();
  const [region, setRegion] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [regionPx, setRegionPx] = useState({ startX: 0, endX: 0 });
  const [trimBtnDisabled, setTrimBtnDisabled] = useState(true);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);

  useEffect(() => {
    const wavesurfer = WaveSurfer.create({
      container: containerRef.current,
      waveColor: '#d9dcff',
      progressColor: '#4353ff',
      height: 100,
      responsive: true,
      plugins: [RegionsPlugin.create()],
    });

    wavesurfer.loadBlob(file);

    wavesurfer.on('ready', () => {
      const dur = wavesurfer.getDuration();
      setDuration(dur);
      const end = Math.min(dur, MAX_CLIP_DURATION);

      const newRegion = wavesurfer.addRegion({
        start: 0,
        end: end,
        color: 'rgba(0, 123, 255, 0.2)',
        drag: true,
        resize: true,
      });

      setRegion(newRegion);
      updateRegionPosition(newRegion);

      const initialDuration = end - 0;
      setTrimBtnDisabled(
        initialDuration < MIN_CLIP_DURATION ||
          initialDuration > MAX_CLIP_DURATION
      );
      if (initialDuration < MIN_CLIP_DURATION) {
        setError(`音频时长不能少于 ${MIN_CLIP_DURATION} 秒`);
      }
      if (initialDuration > MAX_CLIP_DURATION) {
        setError(`音频时长不能超过 ${MAX_CLIP_DURATION / 60} 分钟`);
      }
    });

    wavesurfer.on('audioprocess', (time) => {
      setCurrentTime(time);
    });

    wavesurfer.on('region-updated', (r) => {
      setRegion(r);
      updateRegionPosition(r);

      const trimmedDuration = Math.floor(r.end - r.start);
      if (
        trimmedDuration < MIN_CLIP_DURATION ||
        trimmedDuration > MAX_CLIP_DURATION
      ) {
        setTrimBtnDisabled(true);
        if (trimmedDuration < MIN_CLIP_DURATION) {
          setError(`音频时长不能少于 ${MIN_CLIP_DURATION} 秒`);
          return;
        }
        if (trimmedDuration > MAX_CLIP_DURATION) {
          setError(`音频时长不能超过 ${MAX_CLIP_DURATION / 60} 分钟`);
          return;
        }
      } else {
        setTrimBtnDisabled(false);
        setError(null);
      }
    });

    wavesurfer.on('finish', () => setIsPlaying(false));

    wavesurferRef.current = wavesurfer;

    return () => {
      wavesurfer.destroy();
    };
  }, [file]);

  const updateRegionPosition = (region) => {
    const ws = wavesurferRef.current;
    const containerWidth = containerRef.current.clientWidth;
    const startX = (region.start / ws.getDuration()) * containerWidth;
    const endX = (region.end / ws.getDuration()) * containerWidth;
    setRegionPx({ startX, endX });
  };

  const handlePlayPause = () => {
    if (!region) return;
    const ws = wavesurferRef.current;
    if (!isPlaying) {
      ws.play(region.start, region.end);
    } else {
      ws.pause();
    }
    setIsPlaying(!isPlaying);
  };

  const handleTrimComplete = async () => {
    if (!region || !wavesurferRef.current) return;

    const trimmedLength = Math.floor(region.end - region.start);
    if (trimmedLength < MIN_CLIP_DURATION) {
      setError(`音频时长不能少于 ${MIN_CLIP_DURATION} 秒`);
      return;
    }
    if (trimmedLength > MAX_CLIP_DURATION) {
      setError(`音频时长不能超过 ${MAX_CLIP_DURATION / 60} 分钟`);
      return;
    }
    setError(null);

    setIsLoading(true);
    const audioCtx = new AudioContext();
    const arrayBuffer = await file.arrayBuffer();
    const audioBuffer = await audioCtx.decodeAudioData(arrayBuffer);

    const sampleRate = audioBuffer.sampleRate;
    const startSample = Math.floor(region.start * sampleRate);
    const endSample = Math.floor(region.end * sampleRate);
    const length = endSample - startSample;

    const trimmedBuffer = audioCtx.createBuffer(
      audioBuffer.numberOfChannels,
      length,
      sampleRate
    );

    for (let i = 0; i < audioBuffer.numberOfChannels; i++) {
      const channel = audioBuffer
        .getChannelData(i)
        .slice(startSample, endSample);
      trimmedBuffer.copyToChannel(channel, i, 0);
    }

    const trimmedBlob = await encodeWavBlob(trimmedBuffer, sampleRate);
    onTrimmed(trimmedBlob);
  };

  const encodeWavBlob = (audioBuffer, sampleRate) => {
    const numOfChan = audioBuffer.numberOfChannels;
    const length = audioBuffer.length * numOfChan * 2 + 44;
    const buffer = new ArrayBuffer(length);
    const view = new DataView(buffer);
    const channels = [];
    let offset = 0;
    let pos = 0;

    const writeString = (str) => {
      for (let i = 0; i < str.length; i++) {
        view.setUint8(pos++, str.charCodeAt(i));
      }
    };

    writeString('RIFF');
    view.setUint32(pos, length - 8, true);
    pos += 4;
    writeString('WAVE');
    writeString('fmt ');
    view.setUint32(pos, 16, true);
    pos += 4;
    view.setUint16(pos, 1, true);
    pos += 2;
    view.setUint16(pos, numOfChan, true);
    pos += 2;
    view.setUint32(pos, sampleRate, true);
    pos += 4;
    view.setUint32(pos, sampleRate * 2 * numOfChan, true);
    pos += 4;
    view.setUint16(pos, numOfChan * 2, true);
    pos += 2;
    view.setUint16(pos, 16, true);
    pos += 2;
    writeString('data');
    view.setUint32(pos, length - pos - 4, true);
    pos += 4;

    for (let i = 0; i < audioBuffer.numberOfChannels; i++) {
      channels.push(audioBuffer.getChannelData(i));
    }

    while (pos < length) {
      for (let i = 0; i < numOfChan; i++) {
        let sample = Math.max(-1, Math.min(1, channels[i][offset]));
        sample = sample < 0 ? sample * 0x8000 : sample * 0x7fff;
        view.setInt16(pos, sample, true);
        pos += 2;
      }
      offset++;
    }

    return new Blob([buffer], { type: 'audio/wav' });
  };

  return (
    <div ref={wrapperRef} style={{ position: 'relative' }}>
      <div ref={containerRef} />{' '}
      <div className="timeline-labels">
        <span>00:00</span>
        <span>{formatTime(duration)}</span>
      </div>
      {region && (
        <>
          <div
            style={{
              position: 'absolute',
              top: 110,
              left: regionPx.startX,
              transform: 'translateX(-50%)',
              background: 'rgba(0,0,0,0.7)',
              color: '#fff',
              padding: '2px 6px',
              borderRadius: '4px',
              fontSize: '12px',
              pointerEvents: 'none',
            }}
          >
            {formatTime(region.start)}
          </div>
          <div
            style={{
              position: 'absolute',
              top: 110,
              left: regionPx.endX,
              transform: 'translateX(-50%)',
              background: 'rgba(0,0,0,0.7)',
              color: '#fff',
              padding: '2px 6px',
              borderRadius: '4px',
              fontSize: '12px',
              pointerEvents: 'none',
            }}
          >
            {formatTime(region.end)}
          </div>
        </>
      )}
      {duration > 0 && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: `${(currentTime / duration) * 100}%`,
            transform: 'translateX(-50%)',
            background: 'rgba(0, 0, 0, 0.75)',
            color: 'white',
            padding: '2px 6px',
            borderRadius: '4px',
            fontSize: '12px',
            pointerEvents: 'none',
          }}
        >
          {formatTime(currentTime)}
        </div>
      )}
      {region && (
        <div className="selected-duration">
          当前选中时长：{formatTime(region.end - region.start)}
        </div>
      )}
      {error && (
        <div className="text-danger mb-2" style={{ color: 'red' }}>
          {error}
        </div>
      )}
      <div className="d-flex justify-content-between mt-3">
        <Button onClick={handlePlayPause}>
          {isPlaying ? '暂停' : '播放选中段落'}
        </Button>
        {isLoading ? (
          <LoadingIndicator />
        ) : (
          <Button
            variant="primary"
            onClick={handleTrimComplete}
            disabled={trimBtnDisabled}
          >
            完成剪辑
          </Button>
        )}
      </div>
    </div>
  );
};

export default AudioTrimmer;
